import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  FileText, Plus, Search, Filter, Edit2, Trash2, Send, Copy, Eye, 
  Download, RefreshCw, MoreVertical, DollarSign, Clock, CheckCircle, 
  XCircle, AlertTriangle, X, ChevronDown, Calendar, Percent, MessageSquare,
  FileCheck, FilePlus, ArrowRight, Loader2, ExternalLink, Ban, Bell, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import RecurringClientSelector from './RecurringClientSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft: 'bg-slate-500',
  sent: 'bg-blue-500',
  viewed: 'bg-purple-500',
  partial: 'bg-amber-500',
  paid: 'bg-emerald-500',
  expired: 'bg-red-500',
  canceled: 'bg-gray-600',
  cancelled: 'bg-gray-600',
  declined: 'bg-red-500'
};

const STATUS_ICONS = {
  draft: FileText,
  sent: Send,
  viewed: Eye,
  partial: Clock,
  paid: CheckCircle,
  expired: XCircle,
  cancelled: XCircle,
  declined: XCircle
};

const InvoiceSystem = ({ open, onClose, classTypes = [], initialInvoiceId = null }) => {
  // List state
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [invoiceType, setInvoiceType] = useState('invoice');
  const [customer, setCustomer] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: ''
  });
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState({ discount_type: 'dollar', value: 0, reason: '' });
  const [allowPartialPayments, setAllowPartialPayments] = useState(false);
  const [minimumPayment, setMinimumPayment] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  
  // View invoice dialog
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  
  // Recurring client state
  const [selectedRecurringClient, setSelectedRecurringClient] = useState(null);
  
  // Bulk selection state
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Record payment dialog
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [recordPaymentInvoice, setRecordPaymentInvoice] = useState(null);
  const [recordPaymentAmount, setRecordPaymentAmount] = useState('');
  const [recordPaymentMethod, setRecordPaymentMethod] = useState('cash');
  const [recordPaymentNotes, setRecordPaymentNotes] = useState('');
  const [recordingPayment, setRecordingPayment] = useState(false);
  
  // Add item dialog
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({
    item_type: 'product',
    sku: '',
    name: '',
    description: '',
    quantity: 1,
    unit_price: 0
  });
  
  // Products from POS
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  
  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('invoice_type', typeFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/invoices?${params.toString()}`);
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, searchTerm]);
  
  // Fetch available products (including instructor eCards)
  const fetchProducts = useCallback(async () => {
    try {
      // Fetch WooCommerce products
      const pricesResponse = await axios.get(`${API}/pos/cached-prices`);
      const prices = pricesResponse.data.prices || {};
      const products = Object.entries(prices).map(([sku, data]) => ({
        sku,
        name: data.name,
        price: data.price,
        type: data.type || 'other' // class, addon, fee, or other
      }));
      
      // Also add any class types that aren't in the cached prices (new classes)
      const cachedSkus = new Set(Object.keys(prices));
      classTypes.forEach(ct => {
        if (ct.is_active && ct.sku && !cachedSkus.has(ct.sku)) {
          products.push({
            sku: ct.sku,
            name: ct.name,
            price: 0, // Price unknown - admin can set it manually
            type: 'class'
          });
          console.log(`Added uncached class type: ${ct.name} (${ct.sku})`);
        }
      });
      
      // Fetch instructor eCard pricing (uses instructor_ecard_prices collection)
      try {
        const ecardsResponse = await axios.get(`${API}/instructor-ecards/products`);
        const ecardProducts = ecardsResponse.data || [];
        
        // Add eCards with their instructor pricing
        ecardProducts.forEach(ecard => {
          products.push({
            sku: `ECARD-${ecard.product_number}`,
            name: `Instructor eCard: ${ecard.name}`,
            price: ecard.price || 0, // Uses instructor eCard pricing
            type: 'ecard'
          });
        });
      } catch (ecardError) {
        console.log('No instructor eCard pricing available');
      }
      
      // Sort products: first by category (Classes, Add-ons, Rentals, Disposables, eCards, Fees, Other), then alphabetically
      const categoryOrder = { 'class': 1, 'addon': 2, 'rental': 3, 'disposable': 4, 'ecard': 5, 'fee': 6, 'other': 7 };
      products.sort((a, b) => {
        const catA = categoryOrder[a.type] || 7;
        const catB = categoryOrder[b.type] || 7;
        if (catA !== catB) return catA - catB;
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setAvailableProducts(products);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, [classTypes]);
  
  useEffect(() => {
    if (open) {
      fetchInvoices();
      fetchProducts();
    }
  }, [open, fetchInvoices, fetchProducts]);
  
  // Handle opening a specific invoice when initialInvoiceId is provided
  useEffect(() => {
    if (open && initialInvoiceId && invoices.length > 0) {
      const invoice = invoices.find(inv => inv.invoice_id === initialInvoiceId);
      if (invoice) {
        viewInvoice(initialInvoiceId);  // Pass the invoice ID string, not the object
      }
    }
  }, [open, initialInvoiceId, invoices]);
  
  // Calculate totals
  const calculateSubtotal = () => {
    return items.reduce((sum, item) => {
      const days = item.type === 'rental' ? (item.days || 1) : 1;
      return sum + (item.quantity * item.unit_price * days);
    }, 0);
  };
  
  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();
    const discountValue = parseFloat(discount.value) || 0;
    if (discount.discount_type === 'percent') {
      return subtotal * (discountValue / 100);
    }
    return discountValue;
  };
  
  const calculateTotal = () => {
    return Math.max(0, calculateSubtotal() - calculateDiscount());
  };
  
  // Reset form
  const resetForm = () => {
    setInvoiceType('invoice');
    setCustomer({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      zip_code: ''
    });
    setItems([]);
    setDiscount({ discount_type: 'dollar', value: 0, reason: '' });
    setAllowPartialPayments(false);
    setMinimumPayment('');
    setExpirationDate('');
    setDueDate('');
    setTermsAndConditions('');
    setCustomerNotes('');
    setInternalNotes('');
    setEditingInvoice(null);
  };
  
  // Open editor for new invoice
  const openNewInvoice = (type = 'invoice') => {
    resetForm();
    setInvoiceType(type);
    setShowEditor(true);
  };
  
  // Open editor for existing invoice
  const openEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setInvoiceType(invoice.invoice_type);
    setCustomer(invoice.customer || {});
    setItems(invoice.items || []);
    setDiscount(invoice.discount || { discount_type: 'dollar', value: 0, reason: '' });
    setAllowPartialPayments(invoice.allow_partial_payments || false);
    setMinimumPayment(invoice.minimum_payment?.toString() || '');
    setExpirationDate(invoice.expiration_date ? invoice.expiration_date.split('T')[0] : '');
    setDueDate(invoice.due_date ? invoice.due_date.split('T')[0] : '');
    setTermsAndConditions(invoice.terms_and_conditions || '');
    setCustomerNotes(invoice.customer_notes || '');
    setInternalNotes(invoice.internal_notes || '');
    setShowEditor(true);
  };
  
  // Save invoice
  const saveInvoice = async (status = 'draft') => {
    if (!customer.email) {
      toast.error('Customer email is required');
      return;
    }
    if (items.length === 0) {
      toast.error('Add at least one item');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        invoice_type: invoiceType,
        customer,
        items: items.map(item => ({
          ...item,
          days: item.type === 'rental' ? (item.days || 1) : undefined,
          total: item.quantity * item.unit_price * (item.type === 'rental' ? (item.days || 1) : 1)
        })),
        discount,
        allow_partial_payments: allowPartialPayments,
        minimum_payment: minimumPayment ? parseFloat(minimumPayment) : null,
        expiration_date: expirationDate || null,
        due_date: dueDate || null,
        terms_and_conditions: termsAndConditions,
        customer_notes: customerNotes,
        internal_notes: internalNotes,
        status: status === 'sent' ? 'draft' : status, // Save as draft first, then send
        recurring_client_id: selectedRecurringClient?.id || null  // Link to recurring client for QBO discount sync
      };
      
      let invoiceId;
      
      if (editingInvoice) {
        await axios.put(`${API}/invoices/${editingInvoice.invoice_id}`, payload);
        invoiceId = editingInvoice.invoice_id;
        toast.success('Invoice updated');
      } else {
        const response = await axios.post(`${API}/invoices`, payload);
        invoiceId = response.data.invoice_id;
        toast.success(`${invoiceType === 'quote' ? 'Quote' : 'Invoice'} ${invoiceId} created`);
      }
      
      // If status is 'sent', actually send the email
      if (status === 'sent' && invoiceId) {
        try {
          const sendResponse = await axios.post(`${API}/invoices/${invoiceId}/send`);
          toast.success(sendResponse.data.message || `Email sent to ${customer.email}`);
        } catch (sendError) {
          console.error('Error sending email:', sendError);
          toast.error(sendError.response?.data?.detail || 'Invoice saved but failed to send email');
        }
      }
      
      setShowEditor(false);
      resetForm();
      fetchInvoices();
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  // Delete invoice
  const deleteInvoice = async (invoiceId) => {
    if (!window.confirm('Delete this invoice?')) return;
    
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };
  
  // Send invoice
  const sendInvoice = async (invoiceId) => {
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/send`);
      toast.success(response.data.message);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    }
  };
  
  // Duplicate invoice
  const duplicateInvoice = async (invoiceId) => {
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/duplicate`);
      toast.success(response.data.message);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate');
    }
  };
  
  // Convert quote to invoice
  const convertToInvoice = async (invoiceId) => {
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/convert-to-invoice`);
      toast.success(response.data.message);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert');
    }
  };
  
  // Change invoice status manually
  const changeStatus = async (invoiceId, newStatus) => {
    setChangingStatus(true);
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/change-status`, {
        status: newStatus
      });
      toast.success(response.data.message);
      // Update viewing invoice if open
      if (viewingInvoice?.invoice_id === invoiceId) {
        setViewingInvoice(prev => ({ ...prev, status: newStatus }));
      }
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change status');
    } finally {
      setChangingStatus(false);
    }
  };
  
  // Copy payment link
  const copyPaymentLink = (invoiceId) => {
    const url = `${window.location.origin}/invoice/${invoiceId}`;
    navigator.clipboard.writeText(url);
    toast.success('Payment link copied!');
  };
  
  // Download PDF
  const downloadPdf = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };
  
  // Mark as paid
  const markAsPaid = async (invoiceId) => {
    if (!window.confirm('Mark this invoice as fully paid? This will create a WooCommerce order.')) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/mark-paid`, {
        notes: 'Marked as paid by admin'
      });
      
      if (response.data.success) {
        toast.success(`Invoice marked as paid${response.data.woo_order_id ? ` - WooCommerce Order #${response.data.woo_order_id}` : ''}`);
        fetchInvoices();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };
  
  // Mark as unpaid (reverse payment status)
  const markAsUnpaid = async (invoiceId) => {
    const reason = window.prompt('Reason for marking as unpaid (optional):\n\nThis will clear all payment records and reset the invoice to pending status.');
    if (reason === null) return; // User cancelled
    
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/mark-unpaid`, {
        reason: reason || 'Marked as unpaid by admin'
      });
      
      if (response.data.success) {
        toast.success(`Invoice marked as unpaid - Amount due: $${response.data.amount_due?.toFixed(2)}`);
        fetchInvoices();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as unpaid');
    }
  };
  
  // Cancel invoice
  const cancelInvoice = async (invoiceId) => {
    const reason = window.prompt('Reason for canceling this invoice (optional):\n\nThis will cancel the invoice and the customer will no longer be able to pay it.');
    if (reason === null) return; // User cancelled
    
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/cancel`, {
        reason: reason || 'Canceled by admin'
      });
      
      if (response.data.success) {
        let message = 'Invoice canceled';
        if (response.data.woo_result) {
          message += ` - ${response.data.woo_result}`;
        }
        toast.success(message);
        fetchInvoices();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel invoice');
    }
  };
  
  // Send payment reminder
  const sendReminder = async (invoiceId) => {
    if (!window.confirm('Send a payment reminder email to the customer?')) return;
    
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/send-reminder`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchInvoices();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
  };
  
  // ========== BULK ACTIONS ==========
  
  // Toggle single invoice selection
  const toggleInvoiceSelection = (invoiceId) => {
    setSelectedInvoices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
      } else {
        newSet.add(invoiceId);
      }
      return newSet;
    });
  };
  
  // Select/deselect all invoices
  const toggleSelectAll = () => {
    if (selectedInvoices.size === invoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(invoices.map(inv => inv.invoice_id)));
    }
  };
  
  // Clear selection
  const clearSelection = () => {
    setSelectedInvoices(new Set());
  };
  
  // Bulk change status
  const bulkChangeStatus = async (newStatus) => {
    if (selectedInvoices.size === 0) return;
    
    const statusLabel = newStatus === 'cancelled' ? 'cancel' : `mark as ${newStatus}`;
    if (!window.confirm(`Are you sure you want to ${statusLabel} ${selectedInvoices.size} selected item(s)?`)) return;
    
    setBulkActionLoading(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const invoiceId of selectedInvoices) {
      try {
        if (newStatus === 'cancelled') {
          await axios.post(`${API}/invoices/${invoiceId}/cancel`);
        } else if (newStatus === 'paid') {
          await axios.put(`${API}/invoices/${invoiceId}/status`, { status: 'paid' });
        } else {
          await axios.put(`${API}/invoices/${invoiceId}/status`, { status: newStatus });
        }
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Failed to update ${invoiceId}:`, error);
      }
    }
    
    setBulkActionLoading(false);
    clearSelection();
    
    if (successCount > 0) {
      toast.success(`Successfully updated ${successCount} item(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to update ${failCount} item(s)`);
    }
    
    fetchInvoices();
  };
  
  // Bulk delete
  const bulkDelete = async () => {
    if (selectedInvoices.size === 0) return;
    
    if (!window.confirm(`Are you sure you want to DELETE ${selectedInvoices.size} selected item(s)? This action cannot be undone.`)) return;
    
    setBulkActionLoading(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const invoiceId of selectedInvoices) {
      try {
        await axios.delete(`${API}/invoices/${invoiceId}`);
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Failed to delete ${invoiceId}:`, error);
      }
    }
    
    setBulkActionLoading(false);
    clearSelection();
    
    if (successCount > 0) {
      toast.success(`Successfully deleted ${successCount} item(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} item(s)`);
    }
    
    fetchInvoices();
  };
  
  // Bulk resend
  const bulkResend = async () => {
    if (selectedInvoices.size === 0) return;
    
    if (!window.confirm(`Resend ${selectedInvoices.size} selected invoice(s)/quote(s) to their customers?`)) return;
    
    setBulkActionLoading(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const invoiceId of selectedInvoices) {
      try {
        await axios.post(`${API}/invoices/${invoiceId}/send`);
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Failed to resend ${invoiceId}:`, error);
      }
    }
    
    setBulkActionLoading(false);
    clearSelection();
    
    if (successCount > 0) {
      toast.success(`Successfully sent ${successCount} email(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to send ${failCount} email(s)`);
    }
    
    fetchInvoices();
  };
  
  // Bulk send reminder
  const bulkSendReminder = async () => {
    if (selectedInvoices.size === 0) return;
    
    if (!window.confirm(`Send payment reminder to ${selectedInvoices.size} selected customer(s)?`)) return;
    
    setBulkActionLoading(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const invoiceId of selectedInvoices) {
      try {
        await axios.post(`${API}/invoices/${invoiceId}/send-reminder`);
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Failed to send reminder for ${invoiceId}:`, error);
      }
    }
    
    setBulkActionLoading(false);
    clearSelection();
    
    if (successCount > 0) {
      toast.success(`Successfully sent ${successCount} reminder(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to send ${failCount} reminder(s)`);
    }
    
    fetchInvoices();
  };
  
  // ========== END BULK ACTIONS ==========
  
  // Open record payment dialog
  const openRecordPayment = (invoice) => {
    setRecordPaymentInvoice(invoice);
    setRecordPaymentAmount(invoice.amount_due?.toFixed(2) || '');
    setRecordPaymentMethod('cash');
    setRecordPaymentNotes('');
    setShowRecordPayment(true);
  };
  
  // Submit record payment
  const submitRecordPayment = async () => {
    if (!recordPaymentInvoice) return;
    
    const amount = parseFloat(recordPaymentAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    
    setRecordingPayment(true);
    try {
      const response = await axios.post(`${API}/invoices/${recordPaymentInvoice.invoice_id}/record-payment`, {
        amount,
        payment_method: recordPaymentMethod,
        notes: recordPaymentNotes
      });
      
      if (response.data.success) {
        toast.success(`Payment of $${amount.toFixed(2)} recorded. Status: ${response.data.status}`);
        setShowRecordPayment(false);
        fetchInvoices();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  };
  
  // Remove a specific payment from an invoice
  const removePayment = async (invoiceId, paymentId, amount) => {
    if (!window.confirm(`Remove this payment of $${amount?.toFixed(2)}? This will recalculate the invoice balance.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/invoices/${invoiceId}/payments/${paymentId}`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchInvoices();
        // Update viewing invoice if it's the same one
        if (viewingInvoice?.invoice_id === invoiceId) {
          const updatedInvoice = await axios.get(`${API}/invoices/${invoiceId}`);
          setViewingInvoice(updatedInvoice.data);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove payment');
    }
  };
  
  // Add item from product
  const addProductItem = (product) => {
    const newItem = {
      id: `item-${Date.now()}`,
      item_type: 'product',
      sku: product.sku,
      name: product.name,
      description: '',
      quantity: 1,
      unit_price: product.price,
      type: product.type,
      days: product.type === 'rental' ? 1 : undefined
    };
    setItems([...items, newItem]);
    setShowAddItem(false);
    setProductSearch('');
    toast.success(`Added ${product.name}`);
  };
  
  // Update rental days
  const updateItemDays = (itemId, days) => {
    const newDays = Math.max(1, parseInt(days) || 1);
    setItems(items.map(i => i.id === itemId ? { ...i, days: newDays } : i));
  };
  
  // Add custom item
  const addCustomItem = () => {
    if (!newItem.name || !newItem.unit_price) {
      toast.error('Name and price required');
      return;
    }
    
    const item = {
      id: `item-${Date.now()}`,
      item_type: 'custom',
      sku: null,
      name: newItem.name,
      description: newItem.description,
      quantity: newItem.quantity || 1,
      unit_price: parseFloat(newItem.unit_price)
    };
    setItems([...items, item]);
    setNewItem({ item_type: 'custom', sku: '', name: '', description: '', quantity: 1, unit_price: 0 });
    setShowAddItem(false);
    toast.success('Custom item added');
  };
  
  // Remove item
  const removeItem = (itemId) => {
    setItems(items.filter(i => i.id !== itemId));
  };
  
  // Update item quantity
  const updateItemQuantity = (itemId, quantity) => {
    setItems(items.map(i => i.id === itemId ? { ...i, quantity: Math.max(1, quantity) } : i));
  };
  
  // View invoice details
  const viewInvoice = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}`);
      setViewingInvoice(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to load invoice');
    }
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-[95vw] w-[95vw] h-[90vh] max-h-[90vh] flex flex-col overflow-hidden p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-slate-800 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <FileText className="h-7 w-7 text-teal-400" />
            <h1 className="text-2xl font-bold text-white">Invoices & Quotes</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => openNewInvoice('quote')}
              variant="outline"
              className="border-purple-600 text-white hover:bg-purple-900/30"
            >
              <FilePlus className="h-4 w-4 mr-2" />
              New Quote
            </Button>
            <Button
              onClick={() => openNewInvoice('invoice')}
              className="bg-teal-600 hover:bg-teal-500 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Invoice
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:text-white"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        {/* Filters */}
        <div className="flex items-center gap-4 px-6 py-3 bg-slate-800/50 border-b border-slate-700">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              placeholder="Search by name, email, phone, or invoice #..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-700 border-slate-600 text-white"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-slate-700 border-slate-600 text-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="viewed">Viewed</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="cancelled" className="text-red-400">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40 bg-slate-700 border-slate-600 text-white">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="invoice">Invoices</SelectItem>
              <SelectItem value="quote">Quotes</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="icon"
            onClick={fetchInvoices}
            className="text-slate-400 hover:text-white"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        
        {/* Bulk Actions Toolbar - shows when items are selected */}
        {selectedInvoices.size > 0 && (
          <div className="flex items-center gap-4 px-6 py-3 bg-teal-900/30 border-b border-teal-700">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedInvoices.size === invoices.length}
                onCheckedChange={toggleSelectAll}
                className="border-teal-500 data-[state=checked]:bg-teal-600"
              />
              <span className="text-teal-300 font-medium">
                {selectedInvoices.size} selected
              </span>
            </div>
            
            <div className="flex-1" />
            
            <div className="flex items-center gap-2">
              {/* Change Status Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-slate-600 text-white hover:bg-slate-700"
                    disabled={bulkActionLoading}
                  >
                    <ChevronDown className="h-4 w-4 mr-2" />
                    Change Status
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-slate-800 border-slate-700">
                  <DropdownMenuItem onClick={() => bulkChangeStatus('draft')} className="text-white hover:bg-slate-700">
                    <FileText className="h-4 w-4 mr-2" />
                    Mark as Draft
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => bulkChangeStatus('sent')} className="text-white hover:bg-slate-700">
                    <Send className="h-4 w-4 mr-2" />
                    Mark as Sent
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => bulkChangeStatus('paid')} className="text-emerald-400 hover:bg-emerald-900/30">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark as Paid
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-slate-700" />
                  <DropdownMenuItem onClick={() => bulkChangeStatus('cancelled')} className="text-red-400 hover:bg-red-900/30">
                    <Ban className="h-4 w-4 mr-2" />
                    Cancel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Resend Button */}
              <Button 
                size="sm" 
                variant="outline" 
                className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
                onClick={bulkResend}
                disabled={bulkActionLoading}
              >
                <Send className="h-4 w-4 mr-2" />
                Resend
              </Button>
              
              {/* Send Reminder Button */}
              <Button 
                size="sm" 
                variant="outline" 
                className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                onClick={bulkSendReminder}
                disabled={bulkActionLoading}
              >
                <Bell className="h-4 w-4 mr-2" />
                Send Reminder
              </Button>
              
              {/* Delete Button */}
              <Button 
                size="sm" 
                variant="outline" 
                className="border-red-600 text-red-400 hover:bg-red-900/30"
                onClick={bulkDelete}
                disabled={bulkActionLoading}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
              
              {/* Clear Selection */}
              <Button 
                size="sm" 
                variant="ghost" 
                className="text-slate-400 hover:text-white"
                onClick={clearSelection}
                disabled={bulkActionLoading}
              >
                <X className="h-4 w-4" />
              </Button>
              
              {bulkActionLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-teal-400" />
              )}
            </div>
          </div>
        )}
        
        {/* Invoice List */}
        <ScrollArea className="flex-1">
          <div className="p-6">
            {/* Select All Header (when no selection active) */}
            {invoices.length > 0 && selectedInvoices.size === 0 && (
              <div className="flex items-center gap-3 mb-4 px-2">
                <Checkbox
                  checked={false}
                  onCheckedChange={toggleSelectAll}
                  className="border-slate-500"
                />
                <span className="text-sm text-slate-500">Select all ({invoices.length})</span>
              </div>
            )}
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <FileText className="h-16 w-16 mb-4 opacity-50" />
                <p className="text-lg">No invoices found</p>
                <p className="text-sm mt-2">Create your first invoice or quote to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {invoices
                  .slice() // Create a copy to avoid mutating state
                  .filter(invoice => {
                    // Filter out cancelled from 'all' tab, show only cancelled in 'cancelled' tab
                    if (statusFilter === 'all') {
                      return !['cancelled', 'canceled'].includes(invoice.status);
                    }
                    return true; // Other filters handled by API
                  })
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // Newest first
                  .map(invoice => {
                  const StatusIcon = STATUS_ICONS[invoice.status] || FileText;
                  const isSelected = selectedInvoices.has(invoice.invoice_id);
                  const isCancelled = ['cancelled', 'canceled'].includes(invoice.status);
                  return (
                    <Card 
                      key={invoice.invoice_id} 
                      className={`bg-slate-800/50 border-slate-700 p-4 hover:border-teal-600/50 transition-colors ${isSelected ? 'border-teal-500 bg-teal-900/20' : ''} ${isCancelled ? 'opacity-75' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {/* Checkbox */}
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleInvoiceSelection(invoice.invoice_id)}
                            className="border-slate-500 data-[state=checked]:bg-teal-600"
                            data-testid={`select-${invoice.invoice_id}`}
                          />
                          
                          {/* Status Badge */}
                          <div className={`w-10 h-10 rounded-lg ${STATUS_COLORS[invoice.status]} flex items-center justify-center`}>
                            <StatusIcon className="h-5 w-5 text-white" />
                          </div>
                          
                          {/* Invoice Info */}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`font-mono font-medium ${isCancelled ? 'text-red-400 line-through' : 'text-white'}`}>{invoice.invoice_id}</span>
                              <span className={`px-2 py-0.5 rounded text-xs ${invoice.invoice_type === 'quote' ? 'bg-purple-600/30 text-purple-300' : 'bg-teal-600/30 text-teal-300'} ${isCancelled ? 'line-through' : ''}`}>
                                {invoice.invoice_type}
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs ${STATUS_COLORS[invoice.status]} bg-opacity-30`}>
                                {invoice.status}
                              </span>
                              {invoice.linked_booking_id && (
                                <span className="px-2 py-0.5 rounded text-xs bg-cyan-600/30 text-cyan-300 flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Class
                                </span>
                              )}
                            </div>
                            <p className={`text-sm mt-1 ${isCancelled ? 'text-red-400/70 line-through' : 'text-slate-400'}`}>
                              {invoice.customer?.first_name} {invoice.customer?.last_name} • {invoice.customer?.email}
                            </p>
                          </div>
                        </div>
                        
                        {/* Amount & Actions */}
                        <div className="flex items-center gap-6">
                          {/* Amount */}
                          <div className="text-right">
                            <p className={`text-lg font-bold ${
                              // Canceled/Expired/Declined → Red (takes priority)
                              ['cancelled', 'canceled', 'expired', 'declined'].includes(invoice.status) ? 'text-red-400' :
                              // Paid → Green
                              invoice.status === 'paid' ? 'text-emerald-400' :
                              // Partial → Green with red ring
                              invoice.status === 'partial' ? 'text-emerald-400 ring-2 ring-red-500 rounded px-2' :
                              // Quote type (not canceled/expired/declined) → Blue
                              invoice.invoice_type === 'quote' ? 'text-blue-400' :
                              // All other invoice statuses → White/light
                              'text-slate-100'
                            }`}>${invoice.total?.toFixed(2)}</p>
                            {invoice.amount_paid > 0 && invoice.status !== 'paid' && (
                              <p className="text-xs text-amber-400">
                                Paid: ${invoice.amount_paid?.toFixed(2)} | Due: ${invoice.amount_due?.toFixed(2)}
                              </p>
                            )}
                            <p className="text-xs text-slate-500">{formatDate(invoice.created_at)}</p>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => viewInvoice(invoice.invoice_id)}
                              className="text-slate-400 hover:text-white"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-slate-800 border-slate-700">
                                {invoice.status !== 'paid' && invoice.status !== 'expired' && (
                                  <>
                                    <DropdownMenuItem onClick={() => openEditInvoice(invoice)} className="text-white hover:bg-slate-700">
                                      <Edit2 className="h-4 w-4 mr-2" /> Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => sendInvoice(invoice.invoice_id)} className="text-white hover:bg-slate-700">
                                      <Send className="h-4 w-4 mr-2" /> Send Email
                                    </DropdownMenuItem>
                                  </>
                                )}
                                <DropdownMenuItem onClick={() => copyPaymentLink(invoice.invoice_id)} className="text-white hover:bg-slate-700">
                                  <Copy className="h-4 w-4 mr-2" /> Copy Link
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => downloadPdf(invoice.invoice_id)} className="text-white hover:bg-slate-700">
                                  <Download className="h-4 w-4 mr-2" /> Download PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => duplicateInvoice(invoice.invoice_id)} className="text-white hover:bg-slate-700">
                                  <FilePlus className="h-4 w-4 mr-2" /> Duplicate
                                </DropdownMenuItem>
                                {invoice.invoice_type === 'quote' && invoice.status !== 'expired' && (
                                  <DropdownMenuItem onClick={() => convertToInvoice(invoice.invoice_id)} className="text-white hover:bg-slate-700">
                                    <ArrowRight className="h-4 w-4 mr-2" /> Convert to Invoice
                                  </DropdownMenuItem>
                                )}
                                {/* Payment Actions - for unpaid invoices only */}
                                {invoice.status !== 'paid' && invoice.status !== 'expired' && invoice.status !== 'canceled' && invoice.invoice_type === 'invoice' && (
                                  <>
                                    <DropdownMenuSeparator className="bg-slate-700" />
                                    <DropdownMenuItem onClick={() => sendReminder(invoice.invoice_id)} className="text-blue-400 hover:bg-blue-900/30">
                                      <Bell className="h-4 w-4 mr-2" /> Send Reminder
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openRecordPayment(invoice)} className="text-amber-400 hover:bg-amber-900/30">
                                      <DollarSign className="h-4 w-4 mr-2" /> Record Payment
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => markAsPaid(invoice.invoice_id)} className="text-emerald-400 hover:bg-emerald-900/30">
                                      <CheckCircle className="h-4 w-4 mr-2" /> Mark as Paid
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {/* Mark as Not Paid - for PAID invoices only */}
                                {invoice.status === 'paid' && (
                                  <>
                                    <DropdownMenuSeparator className="bg-slate-700" />
                                    <DropdownMenuItem onClick={() => markAsUnpaid(invoice.invoice_id)} className="text-orange-400 hover:bg-orange-900/30">
                                      <XCircle className="h-4 w-4 mr-2" /> Mark as Not Paid
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {/* Cancel Invoice - for non-paid, non-canceled invoices */}
                                {invoice.status !== 'paid' && invoice.status !== 'canceled' && (
                                  <>
                                    <DropdownMenuSeparator className="bg-slate-700" />
                                    <DropdownMenuItem onClick={() => cancelInvoice(invoice.invoice_id)} className="text-red-400 hover:bg-red-900/30">
                                      <Ban className="h-4 w-4 mr-2" /> Cancel Invoice
                                    </DropdownMenuItem>
                                  </>
                                )}
                                <DropdownMenuSeparator className="bg-slate-700" />
                                {invoice.status !== 'paid' && (
                                  <DropdownMenuItem onClick={() => deleteInvoice(invoice.invoice_id)} className="text-red-400 hover:bg-red-900/30">
                                    <Trash2 className="h-4 w-4 mr-2" /> Delete
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </ScrollArea>
        
        {/* Invoice Editor Dialog */}
        <Dialog open={showEditor} onOpenChange={setShowEditor}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingInvoice ? 'Edit' : 'New'} {invoiceType === 'quote' ? 'Quote' : 'Invoice'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Type Toggle */}
              <div className="flex items-center gap-4">
                <Button
                  variant={invoiceType === 'invoice' ? 'default' : 'outline'}
                  onClick={() => setInvoiceType('invoice')}
                  className={invoiceType === 'invoice' ? 'bg-teal-600' : 'border-slate-600'}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Invoice
                </Button>
                <Button
                  variant={invoiceType === 'quote' ? 'default' : 'outline'}
                  onClick={() => setInvoiceType('quote')}
                  className={invoiceType === 'quote' ? 'bg-purple-600' : 'border-slate-600'}
                >
                  <FileCheck className="h-4 w-4 mr-2" />
                  Quote
                </Button>
              </div>
              
              {/* Customer Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  Customer Information
                </h3>
                
                {/* Recurring Client Selector */}
                <div className="mb-3">
                  <Label className="text-slate-400 flex items-center gap-1 mb-1">
                    <Users className="h-3 w-3" />
                    Use Recurring Client
                  </Label>
                  <RecurringClientSelector
                    selectedClient={selectedRecurringClient}
                    onSelect={(client) => {
                      setSelectedRecurringClient(client);
                      // Auto-fill customer info from recurring client
                      setCustomer({
                        ...customer,
                        first_name: client.first_name || '',
                        last_name: client.last_name || '',
                        email: client.email || '',
                        phone: client.phone || '',
                        company: client.company || ''
                      });
                    }}
                    onClear={() => setSelectedRecurringClient(null)}
                    placeholder="Search recurring clients to auto-fill..."
                    className="[&_input]:bg-slate-700 [&_input]:text-white [&_input]:border-slate-600"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-400">First Name</Label>
                    <Input
                      value={customer.first_name}
                      onChange={(e) => setCustomer({ ...customer, first_name: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400">Last Name</Label>
                    <Input
                      value={customer.last_name}
                      onChange={(e) => setCustomer({ ...customer, last_name: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400">Email *</Label>
                    <Input
                      type="email"
                      value={customer.email}
                      onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400">Phone</Label>
                    <Input
                      value={customer.phone}
                      onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-400">Company</Label>
                    <Input
                      value={customer.company}
                      onChange={(e) => setCustomer({ ...customer, company: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                </div>
              </div>
              
              {/* Items */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-white">Items</h3>
                  <Button
                    size="sm"
                    onClick={() => setShowAddItem(true)}
                    className="bg-teal-600 hover:bg-teal-500"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Item
                  </Button>
                </div>
                
                {items.length === 0 ? (
                  <div className="bg-slate-700/50 rounded-lg p-8 text-center text-slate-500">
                    <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No items added yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {items.map(item => (
                      <div key={item.id} className={`flex items-center gap-3 rounded-lg p-3 ${item.type === 'rental' ? 'bg-purple-900/30 border border-purple-700/50' : 'bg-slate-700/50'}`}>
                        <div className="flex-1">
                          <p className={`font-medium ${item.type === 'rental' ? 'text-purple-200' : 'text-white'}`}>{item.name || item.description}</p>
                          {item.description && item.name && <p className="text-xs text-slate-400">{item.description}</p>}
                          {item.sku && <p className="text-xs text-slate-500">SKU: {item.sku}</p>}
                          {item.type === 'rental' && <p className="text-xs text-purple-400">${item.unit_price.toFixed(2)}/day</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Days input for rentals */}
                          {item.type === 'rental' && (
                            <div className="flex items-center gap-1 bg-purple-800/50 rounded px-2 py-1">
                              <span className="text-xs text-purple-300">Days:</span>
                              <Input
                                type="number"
                                min="1"
                                value={item.days || 1}
                                onChange={(e) => updateItemDays(item.id, e.target.value)}
                                className="w-12 h-7 bg-purple-900/50 border-purple-600 text-purple-200 text-center text-sm"
                              />
                            </div>
                          )}
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateItemQuantity(item.id, e.target.value === '' ? '' : parseInt(e.target.value))}
                            onBlur={(e) => {
                              const val = parseInt(e.target.value);
                              if (isNaN(val) || val < 1) updateItemQuantity(item.id, 1);
                            }}
                            className={`w-16 text-center ${item.type === 'rental' ? 'bg-purple-900/50 border-purple-600 text-purple-200' : 'bg-slate-600 border-slate-500 text-white'}`}
                          />
                          <span className="text-slate-400">×</span>
                          <span className="w-20 text-right text-white">${item.unit_price.toFixed(2)}{item.type === 'rental' ? '' : ''}</span>
                          {item.type === 'rental' && item.days > 1 && (
                            <span className="text-xs text-purple-400">× {item.days}d</span>
                          )}
                          <span className={`w-24 text-right font-medium ${item.type === 'rental' ? 'text-purple-400' : 'text-emerald-400'}`}>
                            ${(item.quantity * item.unit_price * (item.type === 'rental' ? (item.days || 1) : 1)).toFixed(2)}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeItem(item.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Discount */}
              <div className="space-y-3">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Percent className="h-4 w-4" /> Discount
                </h3>
                <div className="flex items-center gap-3">
                  <Select 
                    value={discount.discount_type} 
                    onValueChange={(v) => setDiscount({ ...discount, discount_type: v })}
                  >
                    <SelectTrigger className="w-32 bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="dollar">$ Amount</SelectItem>
                      <SelectItem value="percent">% Percent</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="0"
                    value={discount.value}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      // Allow empty string for user to clear field, parse to number otherwise
                      const numValue = inputValue === '' ? 0 : parseFloat(inputValue);
                      setDiscount(prev => ({ ...prev, value: isNaN(numValue) ? 0 : numValue }));
                    }}
                    placeholder="0"
                    className="w-24 bg-slate-700 border-slate-600 text-white"
                  />
                  <Input
                    value={discount.reason}
                    onChange={(e) => setDiscount({ ...discount, reason: e.target.value })}
                    placeholder="Reason (optional)"
                    className="flex-1 bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              
              {/* Totals */}
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-slate-400">
                  <span>Subtotal</span>
                  <span>${calculateSubtotal().toFixed(2)}</span>
                </div>
                {calculateDiscount() > 0 && (
                  <div className="flex justify-between text-emerald-400">
                    <span>Discount</span>
                    <span>-${calculateDiscount().toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold text-white border-t border-slate-600 pt-2">
                  <span>Total</span>
                  <span>${calculateTotal().toFixed(2)}</span>
                </div>
              </div>
              
              {/* Payment Options */}
              <div className="space-y-3">
                <h3 className="font-semibold text-white">Payment Options</h3>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="partial"
                    checked={allowPartialPayments}
                    onCheckedChange={setAllowPartialPayments}
                  />
                  <Label htmlFor="partial" className="text-white cursor-pointer">
                    Allow partial payments
                  </Label>
                  {allowPartialPayments && (
                    <div className="flex items-center gap-2">
                      <Label className="text-slate-400">Minimum:</Label>
                      <Input
                        type="number"
                        min="0"
                        value={minimumPayment}
                        onChange={(e) => setMinimumPayment(e.target.value)}
                        placeholder="0.00"
                        className="w-24 bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400 flex items-center gap-2">
                    <Calendar className="h-4 w-4" /> Due Date
                  </Label>
                  <Input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" /> Expiration Date
                  </Label>
                  <Input
                    type="date"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              
              {/* Notes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400">Customer Notes (visible to customer)</Label>
                  <Textarea
                    value={customerNotes}
                    onChange={(e) => setCustomerNotes(e.target.value)}
                    placeholder="Notes visible to the customer..."
                    className="bg-slate-700 border-slate-600 text-white h-24"
                  />
                </div>
                <div>
                  <Label className="text-slate-400">Internal Notes (admin only)</Label>
                  <Textarea
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    placeholder="Internal notes..."
                    className="bg-slate-700 border-slate-600 text-white h-24"
                  />
                </div>
              </div>
              
              {/* Terms */}
              <div>
                <Label className="text-slate-400">Terms & Conditions</Label>
                <Textarea
                  value={termsAndConditions}
                  onChange={(e) => setTermsAndConditions(e.target.value)}
                  placeholder="Payment terms, conditions, etc..."
                  className="bg-slate-700 border-slate-600 text-white h-20"
                />
              </div>
            </div>
            
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowEditor(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button
                onClick={() => saveInvoice('draft')}
                disabled={saving}
                variant="outline"
                className="border-slate-600"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Draft
              </Button>
              <Button
                onClick={() => saveInvoice('sent')}
                disabled={saving}
                className="bg-teal-600 hover:bg-teal-500"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Save & Send
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Add Item Dialog */}
        <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Item</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Search Bar */}
              <div>
                <Input
                  placeholder="Search products..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="bg-slate-800 border-slate-600 text-white"
                />
              </div>
              
              {/* Product List */}
              <div>
                <Label className="text-slate-400 mb-2 block">Select Product</Label>
                <ScrollArea className="h-64 border border-slate-700 rounded-lg">
                  <div className="p-2 space-y-1">
                    {/* Group products by category */}
                    {['class', 'addon', 'rental', 'disposable', 'ecard', 'fee', 'other'].map(category => {
                      // Filter by search query
                      const searchLower = productSearch.toLowerCase();
                      const categoryProducts = availableProducts.filter(p => 
                        p.type === category && 
                        (productSearch === '' || 
                         p.name?.toLowerCase().includes(searchLower) || 
                         p.sku?.toLowerCase().includes(searchLower))
                      );
                      if (categoryProducts.length === 0) return null;
                      
                      const categoryLabels = {
                        'class': '📚 Classes',
                        'addon': '➕ Add-ons',
                        'rental': '🔧 Rentals',
                        'disposable': '🧴 Disposable Supplies',
                        'ecard': '🪪 Instructor eCards',
                        'fee': '💰 Fees',
                        'other': '📦 Other'
                      };
                      
                      return (
                        <div key={category}>
                          <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider bg-slate-700/50 rounded sticky top-0">
                            {categoryLabels[category]}
                          </div>
                          {categoryProducts.map(product => (
                            <div
                              key={product.sku}
                              onClick={() => {
                                addProductItem(product);
                                setProductSearch('');
                              }}
                              className="flex items-center justify-between p-2 rounded hover:bg-slate-700 cursor-pointer ml-2"
                            >
                              <div>
                                <p className="text-sm text-white">{product.name}</p>
                                <p className="text-xs text-slate-500">SKU: {product.sku}</p>
                              </div>
                              <span className="text-emerald-400 font-medium">${product.price?.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                    {/* No results message */}
                    {productSearch && availableProducts.filter(p => 
                      p.name?.toLowerCase().includes(productSearch.toLowerCase()) || 
                      p.sku?.toLowerCase().includes(productSearch.toLowerCase())
                    ).length === 0 && (
                      <p className="text-center text-slate-500 py-4">No products match &quot;{productSearch}&quot;</p>
                    )}
                  </div>
                </ScrollArea>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-700"></div>
                <span className="text-xs text-slate-500">or add custom</span>
                <div className="flex-1 h-px bg-slate-700"></div>
              </div>
              
              {/* Custom Item */}
              <div className="space-y-3">
                <div>
                  <Label className="text-slate-400">Item Name</Label>
                  <Input
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    placeholder="Custom item name"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-400">Description</Label>
                  <Input
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder="Optional description"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-400">Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newItem.quantity}
                      onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value === '' ? '' : e.target.value })}
                      onBlur={(e) => {
                        const val = parseInt(e.target.value);
                        if (isNaN(val) || val < 1) setNewItem({ ...newItem, quantity: 1 });
                      }}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400">Unit Price</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newItem.unit_price || ''}
                      onChange={(e) => setNewItem({ ...newItem, unit_price: e.target.value })}
                      placeholder="0.00"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                </div>
                <Button onClick={addCustomItem} className="w-full bg-teal-600 hover:bg-teal-500">
                  Add Custom Item
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* View Invoice Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
            {viewingInvoice && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${viewingInvoice.invoice_type === 'quote' ? 'bg-purple-600' : 'bg-teal-600'}`}>
                      {viewingInvoice.invoice_type}
                    </span>
                    {viewingInvoice.invoice_id}
                    <span className={`px-2 py-1 rounded text-xs ${STATUS_COLORS[viewingInvoice.status]}`}>
                      {viewingInvoice.status}
                    </span>
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* Status Change */}
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <h4 className="text-sm text-slate-400 mb-2">Change Status</h4>
                    <div className="flex items-center gap-2">
                      <Select
                        value={viewingInvoice.status}
                        onValueChange={(newStatus) => changeStatus(viewingInvoice.invoice_id, newStatus)}
                        disabled={changingStatus}
                      >
                        <SelectTrigger className="bg-slate-800 border-slate-600 text-white w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-600">
                          <SelectItem value="draft" className="text-white hover:bg-slate-700">Draft</SelectItem>
                          <SelectItem value="sent" className="text-white hover:bg-slate-700">Sent</SelectItem>
                          <SelectItem value="viewed" className="text-white hover:bg-slate-700">Viewed</SelectItem>
                          <SelectItem value="partial" className="text-white hover:bg-slate-700">Partial</SelectItem>
                          <SelectItem value="paid" className="text-white hover:bg-slate-700">Paid</SelectItem>
                          <SelectItem value="expired" className="text-white hover:bg-slate-700">Expired</SelectItem>
                          <SelectItem value="cancelled" className="text-white hover:bg-slate-700">Cancelled</SelectItem>
                          <SelectItem value="declined" className="text-white hover:bg-slate-700">Declined</SelectItem>
                        </SelectContent>
                      </Select>
                      {changingStatus && <Loader2 className="h-4 w-4 animate-spin text-teal-400" />}
                    </div>
                  </div>
                  
                  {/* Customer */}
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <h4 className="text-sm text-slate-400 mb-2">Customer</h4>
                    <p className="font-medium">{viewingInvoice.customer?.first_name} {viewingInvoice.customer?.last_name}</p>
                    <p className="text-sm text-teal-400">{viewingInvoice.customer?.email}</p>
                    {viewingInvoice.customer?.phone && <p className="text-sm text-slate-400">{viewingInvoice.customer?.phone}</p>}
                  </div>
                  
                  {/* Linked Booking */}
                  {viewingInvoice.linked_booking_id && (
                    <div className="bg-teal-900/30 rounded-lg p-4 border border-teal-600/50">
                      <h4 className="text-sm text-teal-400 mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Linked Class Booking
                      </h4>
                      <p className="text-sm text-slate-300">
                        This invoice is linked to a class booking.
                      </p>
                      {viewingInvoice.customer_notes && (
                        <p className="text-xs text-slate-400 mt-1">{viewingInvoice.customer_notes}</p>
                      )}
                      <p className="text-xs text-slate-500 mt-2 font-mono">
                        Booking ID: {viewingInvoice.linked_booking_id}
                      </p>
                    </div>
                  )}
                  
                  {/* Items */}
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <h4 className="text-sm text-slate-400 mb-3">Items</h4>
                    <div className="space-y-2">
                      {viewingInvoice.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span>{item.name || item.description} × {item.quantity}</span>
                          <span className="text-emerald-400">${(item.quantity * item.unit_price).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-slate-600 mt-3 pt-3 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Subtotal</span>
                        <span>${viewingInvoice.subtotal?.toFixed(2)}</span>
                      </div>
                      {viewingInvoice.discount_amount > 0 && (
                        <div className="flex justify-between text-sm text-emerald-400">
                          <span>Discount</span>
                          <span>-${viewingInvoice.discount_amount?.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-lg">
                        <span>Total</span>
                        <span>${viewingInvoice.total?.toFixed(2)}</span>
                      </div>
                      {viewingInvoice.amount_paid > 0 && (
                        <>
                          <div className="flex justify-between text-sm text-blue-400">
                            <span>Paid</span>
                            <span>${viewingInvoice.amount_paid?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm text-amber-400">
                            <span>Due</span>
                            <span>${viewingInvoice.amount_due?.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Payments */}
                  {viewingInvoice.payments?.length > 0 && (
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <h4 className="text-sm text-slate-400 mb-3">Payment History</h4>
                      <div className="space-y-2">
                        {viewingInvoice.payments.map((payment, idx) => (
                          <div key={idx} className="flex justify-between items-center text-sm bg-slate-800/50 rounded p-2">
                            <div className="flex-1">
                              <span className="text-emerald-400 font-medium">${payment.amount?.toFixed(2)}</span>
                              <span className="text-slate-500 ml-2">via {payment.payment_method}</span>
                              {payment.transaction_id && (
                                <span className="text-slate-600 ml-2 text-xs">({payment.transaction_id.substring(0, 12)}...)</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500 text-xs">{formatDate(payment.created_at)}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removePayment(viewingInvoice.invoice_id, payment.id, payment.amount)}
                                className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                                title="Remove this payment"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Comments */}
                  {viewingInvoice.comments?.length > 0 && (
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <h4 className="text-sm text-slate-400 mb-3 flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" /> Comments
                      </h4>
                      <div className="space-y-3">
                        {viewingInvoice.comments.map((comment, idx) => (
                          <div key={idx} className={`p-3 rounded-lg ${comment.author === 'admin' ? 'bg-teal-900/30' : 'bg-slate-600/50'}`}>
                            <div className="flex justify-between text-xs text-slate-400 mb-1">
                              <span>{comment.author_name}</span>
                              <span>{formatDate(comment.created_at)}</span>
                            </div>
                            <p className="text-sm">{comment.message}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* WooCommerce Order */}
                  {viewingInvoice.woo_order_id && (
                    <div className="bg-emerald-900/30 rounded-lg p-4">
                      <p className="text-sm text-emerald-400 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        WooCommerce Order #{viewingInvoice.woo_order_id}
                      </p>
                    </div>
                  )}
                  
                  {/* Activity Log */}
                  {viewingInvoice.activity_log?.length > 0 && (
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <h4 className="text-sm text-slate-400 mb-3 flex items-center gap-2">
                        <Clock className="h-4 w-4" /> Activity Log
                      </h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {[...viewingInvoice.activity_log].reverse().map((activity, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-xs border-l-2 border-slate-600 pl-3 py-1">
                            <div className="flex-1">
                              <span className={`font-medium ${
                                activity.actor === 'admin' ? 'text-teal-400' : 
                                activity.actor === 'customer' ? 'text-purple-400' : 'text-slate-400'
                              }`}>
                                {activity.actor === 'admin' ? '👤 Admin' : activity.actor === 'customer' ? '👥 Customer' : activity.actor}
                              </span>
                              <span className="text-slate-500 ml-2">
                                {new Date(activity.timestamp).toLocaleString()}
                              </span>
                              <p className="text-slate-300 mt-0.5">{activity.details}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <DialogFooter className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => copyPaymentLink(viewingInvoice.invoice_id)}
                    className="border-slate-600"
                  >
                    <Copy className="h-4 w-4 mr-2" /> Copy Link
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => downloadPdf(viewingInvoice.invoice_id)}
                    className="border-slate-600"
                  >
                    <Download className="h-4 w-4 mr-2" /> Download PDF
                  </Button>
                  <Button
                    onClick={() => {
                      window.open(`/invoice/${viewingInvoice.invoice_id}?viewer=admin`, '_blank');
                    }}
                    className="bg-teal-600 hover:bg-teal-500"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" /> View Payment Page
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Record Payment Dialog */}
        <Dialog open={showRecordPayment} onOpenChange={setShowRecordPayment}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-amber-400" />
                Record Payment
              </DialogTitle>
            </DialogHeader>
            
            {recordPaymentInvoice && (
              <div className="space-y-4 py-4">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400">Invoice</p>
                  <p className="font-medium">{recordPaymentInvoice.invoice_id}</p>
                  <p className="text-slate-400 mt-2">
                    {recordPaymentInvoice.customer?.first_name} {recordPaymentInvoice.customer?.last_name}
                  </p>
                  <div className="flex justify-between mt-3 pt-3 border-t border-slate-600">
                    <span className="text-slate-400">Amount Due</span>
                    <span className="font-bold text-lg">${recordPaymentInvoice.amount_due?.toFixed(2)}</span>
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-300">Payment Amount</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      type="number"
                      min="0.01"
                      max={recordPaymentInvoice.amount_due}
                      step="0.01"
                      value={recordPaymentAmount}
                      onChange={(e) => setRecordPaymentAmount(e.target.value)}
                      className="pl-10 bg-slate-700 border-slate-600 text-white"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-300">Payment Method</Label>
                  <Select value={recordPaymentMethod} onValueChange={setRecordPaymentMethod}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="cash" className="text-white">Cash</SelectItem>
                      <SelectItem value="check" className="text-white">Check</SelectItem>
                      <SelectItem value="bank_transfer" className="text-white">Bank Transfer</SelectItem>
                      <SelectItem value="other" className="text-white">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-300">Notes (Optional)</Label>
                  <Input
                    value={recordPaymentNotes}
                    onChange={(e) => setRecordPaymentNotes(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="e.g., Check #1234"
                  />
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowRecordPayment(false)}
                className="border-slate-600"
              >
                Cancel
              </Button>
              <Button
                onClick={submitRecordPayment}
                disabled={recordingPayment || !recordPaymentAmount}
                className="bg-amber-600 hover:bg-amber-500"
              >
                {recordingPayment ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Recording...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Record Payment
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceSystem;
